import { __assign } from "tslib";
import PropTypes from 'prop-types';
import React from 'react';
import cx from 'classnames';
import toClass from 'recompose/toClass';
import { OverlayPosition } from '@marvelapp/core';
import Image from './Image';
import styles from './Overlay.module.css';
import controlScroll from '../hocs/controlScroll';
import * as screenLocation from '../constants/screenLocation';
import OverlayScrollableArea from '../containers/OverlayScrollableArea';
import OverlayHeader from '../containers/OverlayHeader';
import OverlayFooter from '../containers/OverlayFooter';
var BaseOverlay = function (_a) {
    var _b;
    var screenID = _a.screenID, transition = _a.transition, position = _a.position, top = _a.top, left = _a.left, width = _a.width, height = _a.height, imgURL = _a.imgURL, hotspots = _a.hotspots, annotations = _a.annotations, getScreenScrollPos = _a.getScreenScrollPos, scrollTop = _a.scrollTop, statusBarTrimHeight = _a.statusBarTrimHeight, renderedScreenDimensions = _a.renderedScreenDimensions, imageWidth = _a.imageWidth, imageMargin = _a.imageMargin, onClick = _a.onClick, onBackgroundClick = _a.onBackgroundClick, withRef = _a.withRef, getScrollPos = _a.getScrollPos, isScrollable = _a.isScrollable, id = _a.id, isCurrentScreen = _a.isCurrentScreen;
    if (!id) {
        return React.createElement("span", null);
    }
    var imgStyle = __assign({ backgroundImage: "url(" + imgURL + ")", width: width,
        height: height }, getPositionStyles({
        positionCode: position,
        width: width,
        height: height,
        screenWidth: imageWidth,
        screenHeight: renderedScreenDimensions.height,
        top: top,
        left: left,
        scrollTop: scrollTop,
        statusBarTrimHeight: statusBarTrimHeight,
    }));
    var containerStyle = __assign({ top: -renderedScreenDimensions.height, height: renderedScreenDimensions.height, overflowY: 'hidden' }, getScrollableProps(imgStyle, imageWidth, imageMargin, width, renderedScreenDimensions));
    var containerClass = cx(styles.container, styles[transition], (_b = {},
        _b[styles.scrollable] = isScrollable,
        _b));
    var getOverlayScrollPos = isScrollable ? getScrollPos : function () { return 0; };
    return (React.createElement("div", { className: containerClass, onClick: onBackgroundClick, style: containerStyle },
        isScrollable && (React.createElement("div", { "data-layer-type": "overlay", onClick: function (e) { return onClick(screenLocation.BODY, e); }, style: {
                marginLeft: imgStyle.left,
                marginTop: Math.max(0, imgStyle.top),
                height: height,
            } },
            React.createElement(OverlayScrollableArea, { height: height, isCurrentScreen: isCurrentScreen, screenID: screenID, width: width, withRef: withRef },
                React.createElement(Image, __assign({ annotations: annotations, getOverlayScrollPos: getOverlayScrollPos, getScreenScrollPos: getScreenScrollPos, hotspots: hotspots, screenID: screenID, src: imgURL, type: screenLocation.OVERLAY }, __assign(__assign({}, imgStyle), { left: 0, top: 0 })))),
            React.createElement(OverlayHeader, { getOverlayScrollPos: getOverlayScrollPos, getScreenScrollPos: getScreenScrollPos, height: height, isCurrentScreen: isCurrentScreen, onClick: function (e) { return onClick(screenLocation.HEADER, e); }, screenID: screenID, width: width }),
            React.createElement(OverlayFooter, { getOverlayScrollPos: getOverlayScrollPos, getScreenScrollPos: getScreenScrollPos, height: height, isCurrentScreen: isCurrentScreen, onClick: function (e) { return onClick(screenLocation.FOOTER, e); }, screenID: screenID, width: width }))),
        !isScrollable && (React.createElement(Image, __assign({ annotations: annotations, "data-layer-type": "overlay", getOverlayScrollPos: getOverlayScrollPos, getScreenScrollPos: getScreenScrollPos, hotspots: hotspots, id: "currentOverlay", onClick: function (e) { return onClick(screenLocation.BODY, e); }, screenID: screenID, src: imgURL, type: screenLocation.OVERLAY }, imgStyle)))));
};
BaseOverlay.propTypes = {
    imgURL: PropTypes.string.isRequired,
    position: PropTypes.oneOf([
        OverlayPosition.Custom,
        OverlayPosition.Centered,
        OverlayPosition.TopLeft,
        OverlayPosition.TopCenter,
        OverlayPosition.TopRight,
        OverlayPosition.BottomLeft,
        OverlayPosition.BottomCenter,
        OverlayPosition.BottomRight,
    ]).isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    top: PropTypes.number,
    left: PropTypes.number,
    imageWidth: PropTypes.number.isRequired,
    imageMargin: PropTypes.object.isRequired,
    hotspots: PropTypes.arrayOf(PropTypes.object),
    annotations: PropTypes.arrayOf(PropTypes.object),
    getScreenScrollPos: PropTypes.func.isRequired,
    onClick: PropTypes.func,
    onBackgroundClick: PropTypes.func,
    renderedScreenDimensions: PropTypes.shape({
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
    }),
    transition: PropTypes.string,
    scrollTop: PropTypes.number,
    statusBarTrimHeight: PropTypes.number.isRequired,
    withRef: PropTypes.object,
    getScrollPos: PropTypes.func,
    isScrollable: PropTypes.bool,
    screenID: PropTypes.number,
};
// TODO move to a utils
export function getPositionStyles(_a) {
    var positionCode = _a.positionCode, width = _a.width, height = _a.height, screenWidth = _a.screenWidth, screenHeight = _a.screenHeight, top = _a.top, left = _a.left, scrollTop = _a.scrollTop, statusBarTrimHeight = _a.statusBarTrimHeight;
    switch (positionCode) {
        case OverlayPosition.Custom: {
            var shiftedTop = top - statusBarTrimHeight;
            if (scrollTop) {
                shiftedTop -= scrollTop;
            }
            return {
                top: shiftedTop,
                left: left,
            };
        }
        case OverlayPosition.Centered:
            return {
                top: (screenHeight - height) / 2,
                left: (screenWidth - width) / 2,
            };
        case OverlayPosition.TopLeft:
            return {
                left: 0,
                top: 0,
            };
        case OverlayPosition.TopCenter:
            return {
                left: (screenWidth - width) / 2,
                top: 0,
            };
        case OverlayPosition.TopRight:
            return {
                left: screenWidth - width,
                top: 0,
            };
        case OverlayPosition.BottomLeft:
            return {
                left: 0,
                top: screenHeight - height,
            };
        case OverlayPosition.BottomCenter:
            return {
                left: (screenWidth - width) / 2,
                top: screenHeight - height,
            };
        case OverlayPosition.BottomRight:
            return {
                left: screenWidth - width,
                top: screenHeight - height,
            };
        default:
            throw new Error("'" + positionCode + "' is not a valid overlay position");
    }
}
// TODO do we need this?? Elle, what's it for again?
function getScrollableProps(imgStyle, imageWidth, imageMargin, width) {
    // if the overlay width is wider than base image we want to show the whole image, otherwise contain and position it inside the base image size
    if (width > imageWidth) {
        return {
            width: width,
            marginLeft: imgStyle.marginLeft,
            left: imgStyle.left,
        };
    }
    return {
        width: imageWidth,
        marginLeft: imageMargin.marginLeft,
    };
}
var Overlay = controlScroll(toClass(BaseOverlay));
export default Overlay;
